/**
 * CrmNewLeadWizard — multi-step lead entry, mirroring the legacy "New lead" flow:
 *   1. Who is this lead?  (salutation/first/last/org/email/phone[auto-format]/website)
 *   2. Where is the project?  (street/city/state/zip)
 *   3. How did they find us?  (Lead Source pills + branching sub-fields + details)
 *   4. Project details  (description) + summary
 * Writes the lead via services/leads.createLead with Lead Status = 'New'.
 */
import { useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import ContactPicker from './ContactPicker.jsx'
import { alertDialog } from '../Dialog.jsx'
import { ctx } from '../../outline/ctx.js'
import {
  createLead, SALUTATIONS, LEAD_SOURCES, INBOUND_OPTIONS, EXISTING_DB_OPTIONS,
} from '../../services/leads.js'
import { logInteraction, interactionForLeadSource } from '../../services/interactions.js'
import { upsertLeadJudgment } from '../../services/leadJudgment.js'
import { splitAddress } from '../../lib/addressSplit.js'

const REFERRER_SOURCES = new Set(['Referral', 'Networking', 'Event Attendance'])

// Screening at the source — classifying the contact AT INTAKE is what keeps a networking
// partner (the Beth Gale case) from being funneled into client-conversion pipeline pressure.
// A non-client kind presets the judgment layer's not_a_client_type on creation, so the detail
// modal's Next-Step card immediately guides to the close-out (which can add them to Contacts)
// instead of the acknowledge/qualify cadence.
const CONTACT_KINDS = [
  { key: 'client', label: 'Prospective client' },
  { key: 'trade_or_referral_partner', label: 'Trade / referral partner' },
  { key: 'vendor_or_consultant', label: 'Vendor or consultant' },
  { key: 'unsure', label: 'Not sure yet' },
]
const NON_CLIENT_KINDS = new Set(['trade_or_referral_partner', 'vendor_or_consultant'])

const STEPS = [
  { title: 'Who is this lead?', sub: 'The person or household making the inquiry.' },
  { title: 'Where is the project?', sub: 'Location helps us research and qualify.' },
  { title: 'How did they find us?', sub: 'Where this lead came from.' },
  { title: 'Project details', sub: 'Anything we know about the scope. Review and create.' },
]

// Full state names (Airtable single-select stores full names); show name, store name.
export const STATE_OPTIONS = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming']

function fmtPhone(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 10)
  if (d.length < 4) return d
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

const BLANK = {
  salutation: '', firstName: '', lastName: '', organization: '', email: '', phone: '', website: '',
  street: '', city: '', state: '', zip: '',
  source: '', inboundOption: '', existingDbOption: '', sourceDetails: '',
  referredById: '', referredByName: '',
  description: '',
}

export default function CrmNewLeadWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0)
  const [f, setF] = useState({ ...BLANK })
  const [kind, setKind] = useState('client')
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))

  const nonClient = NON_CLIENT_KINDS.has(kind)
  const emailOk = !f.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())
  const canNext = (
    step === 0 ? !!f.firstName.trim() && emailOk :
    // A relationship contact has no project — location is optional for them.
    step === 1 ? (nonClient || (!!f.city.trim() && !!f.state)) :
    step === 2 ? !!f.source &&
      (f.source !== 'Inbound Inquiry' || !!f.inboundOption) &&
      (f.source !== 'Existing Database' || !!f.existingDbOption)
    : true
  )

  async function create() {
    if (busy) return
    setBusy(true)
    try {
      const patch = { ...f, status: 'New' }
      // Clear sub-fields not relevant to the chosen source.
      if (f.source !== 'Inbound Inquiry') patch.inboundOption = ''
      if (f.source !== 'Existing Database') patch.existingDbOption = ''
      if (!REFERRER_SOURCES.has(f.source)) { patch.referredById = ''; patch.referredByName = '' }
      const lead = await createLead(patch)
      if (lead && nonClient) {
        // Preset judgment axis 4 so the detail modal opens straight into "close out" guidance
        // instead of the client-qualification cadence. Best-effort — a failed judgment write
        // leaves an ordinary lead, never blocks creation.
        upsertLeadJudgment(lead.id, { not_a_client_type: kind })
          .catch(e => console.warn('[new-lead] judgment preset failed', e))
      }
      if (lead) {
        // Best-effort: how this lead arrived IS its first interaction — log it now so the
        // engine's "no human touch yet" rule doesn't fire universally on every new lead (see
        // interactionForLeadSource's own doc comment). A failed log must never undo lead
        // creation, so this never throws into the outer catch.
        const { type, leadActivityType } = interactionForLeadSource(f.source, f.inboundOption)
        logInteraction({
          leadId: lead.id, type, leadActivityType,
          notes: f.sourceDetails || undefined,
          contactIds: f.referredById ? [f.referredById] : undefined,
          teamMemberId: (ctx.currentMember && ctx.currentMember.id) || undefined,
        }).catch(e => console.warn('[new-lead] initial interaction log failed', e))
      }
      if (lead && onCreated) onCreated(lead)
      onClose && onClose()
    } catch (e) {
      await alertDialog({ title: 'Create failed', message: (e && e.message) || 'Could not create the lead.' })
    } finally { setBusy(false) }
  }

  const footer = (
    <>
      {step > 0 && <button style={F.btnGhost} onClick={() => setStep(s => s - 1)} disabled={busy}>Back</button>}
      <div style={{ flex: 1 }} />
      {step < 3
        ? <button style={F.btnPrimary} onClick={() => setStep(s => s + 1)} disabled={!canNext}>{step === 2 ? 'Review' : 'Continue'}</button>
        : <button style={F.btnPrimary} onClick={create} disabled={busy}>{busy ? 'Creating…' : 'Create lead'}</button>}
    </>
  )

  return (
    <CrmModal title="New lead" onClose={onClose} footer={footer} width={580}>
      <div style={S.dots}>
        {STEPS.map((_, i) => <span key={i} style={{ ...S.dot, ...(i === step ? S.dotActive : i < step ? S.dotDone : {}) }} />)}
      </div>
      <div style={S.stepLabel}>Step {step + 1} of 4</div>
      <div style={S.stepTitle}>{STEPS[step].title}</div>
      <div style={S.stepSub}>{step === 1 && nonClient ? 'Optional for a relationship contact. Add a location only if one matters.' : STEPS[step].sub}</div>

      {step === 0 && (
        <>
          <label style={F.label}>Who is this? *</label>
          <div style={S.pills}>
            {CONTACT_KINDS.map(k => (
              <button key={k.key} style={pill(kind === k.key)} onClick={() => setKind(k.key)}>{k.label}</button>
            ))}
          </div>
          {nonClient && (
            <div style={S.kindHint}>
              Leads exist to answer one question: is there a real opportunity? A relationship contact
              skips that funnel — after creation you'll be guided to a quick close-out, which can add
              them to the Contacts directory so the relationship is kept without the pipeline pressure.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 12 }}>
            <div style={F.row}>
              <label style={F.label}>Title</label>
              <select style={F.input} value={f.salutation} onChange={e => set('salutation', e.target.value)}>
                <option value="">—</option>{SALUTATIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={F.row}><label style={F.label}>First name *</label><input autoFocus style={F.input} value={f.firstName} onChange={e => set('firstName', e.target.value)} /></div>
            <div style={F.row}><label style={F.label}>Last name</label><input style={F.input} value={f.lastName} onChange={e => set('lastName', e.target.value)} /></div>
          </div>
          <div style={F.row}><label style={F.label}>Organization</label><input style={F.input} value={f.organization} onChange={e => set('organization', e.target.value)} placeholder="Household or company (optional)" /></div>
          <div style={F.grid2}>
            <div style={F.row}><label style={F.label}>Email</label><input style={{ ...F.input, borderColor: emailOk ? undefined : 'rgba(179,38,30,0.6)' }} type="email" value={f.email} onChange={e => set('email', e.target.value)} /></div>
            <div style={F.row}><label style={F.label}>Phone</label><input style={F.input} value={f.phone} onChange={e => set('phone', fmtPhone(e.target.value))} placeholder="(000) 000-0000" /></div>
          </div>
          <div style={F.row}><label style={F.label}>Website</label><input style={F.input} type="url" value={f.website} onChange={e => set('website', e.target.value)} placeholder="https://" /></div>
        </>
      )}

      {step === 1 && (
        <>
          <div style={F.row}><label style={F.label}>Street address</label><input autoFocus style={F.input} value={f.street} onChange={e => set('street', e.target.value)}
            onPaste={e => {
              // A whole pasted address fills Street, City, State and Zip (lib/addressSplit.js).
              const parts = splitAddress(e.clipboardData && e.clipboardData.getData('text'))
              if (!parts) return
              e.preventDefault()
              set('street', parts.street); set('city', parts.city); set('state', parts.state)
              if (parts.zip) set('zip', parts.zip)
            }} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px', gap: 12 }}>
            <div style={F.row}><label style={F.label}>City *</label><input style={F.input} value={f.city} onChange={e => set('city', e.target.value)} /></div>
            <div style={F.row}>
              <label style={F.label}>State *</label>
              <select style={F.input} value={f.state} onChange={e => set('state', e.target.value)}>
                <option value="">—</option>{STATE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={F.row}><label style={F.label}>Zip</label><input style={F.input} value={f.zip} onChange={e => set('zip', e.target.value)} /></div>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <label style={F.label}>Lead source *</label>
          <div style={S.pills}>
            {LEAD_SOURCES.map(s => (
              <button key={s} style={pill(f.source === s)} onClick={() => {
                set('source', s)
                if (!REFERRER_SOURCES.has(s)) { set('referredById', ''); set('referredByName', '') }
              }}>{s}</button>
            ))}
          </div>
          {f.source === 'Inbound Inquiry' && (
            <div style={F.row}>
              <label style={F.label}>Inquiry channel *</label>
              <select style={F.input} value={f.inboundOption} onChange={e => set('inboundOption', e.target.value)}>
                <option value="">—</option>{INBOUND_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}
          {f.source === 'Existing Database' && (
            <div style={F.row}>
              <label style={F.label}>Database category *</label>
              <select style={F.input} value={f.existingDbOption} onChange={e => set('existingDbOption', e.target.value)}>
                <option value="">—</option>{EXISTING_DB_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}
          {REFERRER_SOURCES.has(f.source) && (
            <div style={F.row}>
              <label style={F.label}>Referring contact</label>
              <ContactPicker
                value={f.referredById} name={f.referredByName}
                onChange={(id, nm) => { set('referredById', id); set('referredByName', nm) }}
              />
              <div style={S.pickerHint}>Not in contacts yet? Add their name in the notes below.</div>
            </div>
          )}
          <div style={{ ...F.row, marginTop: 12 }}>
            <label style={F.label}>Additional details</label>
            <textarea style={F.textarea} value={f.sourceDetails} onChange={e => set('sourceDetails', e.target.value)}
              placeholder={f.source === 'Other' ? 'Describe how they heard about us…' : 'Any additional context about how they found us…'} />
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div style={F.row}><label style={F.label}>Project scope / notes</label><textarea autoFocus style={{ ...F.textarea, minHeight: 96 }} value={f.description} onChange={e => set('description', e.target.value)} placeholder="What do we know about the project?" /></div>
          <div style={S.summary}>
            <div style={S.summaryLabel}>New lead</div>
            <SummaryRow k="Contact kind" v={(CONTACT_KINDS.find(k => k.key === kind) || {}).label || '—'} />
            <SummaryRow k="Name" v={`${f.salutation} ${f.firstName} ${f.lastName}`.trim() || '—'} />
            <SummaryRow k="Organization" v={f.organization || '—'} />
            <SummaryRow k="Contact" v={[f.email, f.phone].filter(Boolean).join(' · ') || '—'} />
            <SummaryRow k="Location" v={[f.city, f.state].filter(Boolean).join(', ') || '—'} />
            <SummaryRow k="Source" v={[f.source, f.inboundOption || f.existingDbOption, f.referredByName].filter(Boolean).join(' · ') || '—'} />
          </div>
        </>
      )}
    </CrmModal>
  )
}

function SummaryRow({ k, v }) {
  return (
    <div style={S.sRow}>
      <div style={S.sKey}>{k}</div>
      <div style={S.sVal}>{v}</div>
    </div>
  )
}

function pill(on) {
  return {
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: on ? 600 : 400,
    color: on ? '#fff' : 'var(--aj-text-mid)', background: on ? '#07272D' : 'var(--aj-bg-muted)',
    border: 'none', borderRadius: 20, padding: '6px 12px', cursor: 'pointer',
  }
}

const S = {
  dots: { display: 'flex', gap: 6, marginBottom: 14 },
  dot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--aj-border-mid)' },
  dotActive: { background: 'var(--aj-text)' },
  dotDone: { background: 'var(--aj-text-mid)' },
  stepLabel: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 4 },
  stepTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 17, fontWeight: 400, color: 'var(--aj-text)', marginBottom: 3 },
  stepSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', marginBottom: 18 },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  kindHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', lineHeight: 1.5, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 14 },
  pickerHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 5 },
  summary: { background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: 14, marginTop: 6 },
  summaryLabel: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 10 },
  sRow: { display: 'flex', gap: 12, padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)' },
  sKey: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', width: 96, flexShrink: 0 },
  sVal: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)' },
}
